/*
 * Copyright 2022 ThoughtWorks, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import _ from "lodash";
import m from "mithril";

type Provider<T> = () => T;
type Predicate<T> = (v: T) => boolean;

export function showIf(condition: boolean, content: () => m.Children) {
  if (condition) {
    return content();
  }
}

/**
 * Wraps a set of providers as a single provider of the same type. The resultant provider will lazily
 * evaluate each provider within, testing each provider output against a specified criterion/predicate
 * and return the first output that passes. If none pass after all wrapped providers are exhausted, the
 * last output is returned as the final value. This is similar to a find first operation that operates
 * on the output of each provider rather than on the provider itself.
 *
 * This requires at least one provider, specified as the param `initial`
 *
 * @param criterion the predicate to test for the first acceptable output
 * @param initial the first provider
 * @param subsequent any subsequent providers (optional)
 *
 * @returns a provider that returns the first acceptable value of the wrapped providers
 */
export function cascading<T>(criterion: Predicate<T>, initial: Provider<T>, ...subsequent: Array<Provider<T>>): Provider<T> {
  return () => {
    let val = initial();
    for (let i = 0, len = subsequent.length; !criterion(val) && i < len; i++) {
      val = subsequent[i]();
    }
    return val;
  };
}

/**
 * Executes transforms in series on an input and returns the final result. The output of each transform.
 * The typings constructed such that the result type matches the input type, but this will theoretically
 * work if you want to transform into another type as well, so long as each transform's input matches its
 * prior sibling's output type. You may have to trick typescript into skipping the typechecks of your
 * transforms and output, though.
 *
 * @param input the initial value
 * @param transforms the set of transformers
 *
 * @returns the value after all transforms are applied in order
 */
export function pipeline<T>(input: T, ...transforms: Array<(v: T) => T>): T {
  return _.reduce(transforms, (memo, f) => f(memo), input);
}
